import { useState } from 'react';
import { Button, Chip, Stack, Snackbar, Alert, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import DataTable from '../components/DataTable';
import { officerNav } from '../components/officerNav';
import usePaginatedList from '../hooks/usePaginatedList';
import api from '../services/api';

const STATUS_COLOR = { DRAFT: 'default', PUBLISHED: 'success', CLOSED: 'warning', CANCELLED: 'error' };

const FILTERS = {
  active: { status: 'PUBLISHED' },
  drafts: { status: 'DRAFT' },
  needsLocation: { isHistorical: 'true', missingLocation: 'true' },
  historical: { isHistorical: 'true' },
};

export default function ManageTenders() {
  const navigate = useNavigate();
  const [toast, setToast] = useState('');
  const list = usePaginatedList('/tenders', { pageSize: 25, filters: FILTERS });

  const act = async (id, action) => {
    try {
      if (action === 'cancel') {
        const reason = window.prompt('Reason for cancellation?');
        if (!reason) return;
        await api.post(`/tenders/${id}/cancel`, { reason });
      } else {
        await api.post(`/tenders/${id}/${action}`);
      }
      setToast(`Tender ${action}${action.endsWith('e') ? 'd' : 'ed'}`);
      list.reload();
    } catch (err) {
      setToast(err.response?.data?.error?.message || 'Action failed');
    }
  };

  const columns = [
    { key: 'reference', header: 'Reference' },
    { key: 'title', header: 'Title' },
    {
      key: 'location',
      header: 'Location',
      render: (t) => {
        if (t.location) return t.location;
        if (t.isHistorical) {
          return <Typography variant="body2" color="warning.main">Not recorded</Typography>;
        }
        return '—';
      },
    },
    {
      key: 'status',
      header: 'Status',
      render: (t) => <Chip size="small" label={t.status} color={STATUS_COLOR[t.status]} />,
    },
    {
      key: 'deadline',
      header: 'Deadline',
      render: (t) => (t.submissionDeadline
        ? new Date(t.submissionDeadline).toLocaleDateString('en-GB')
        : '—'),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: (t) => (
        <>
          <Button size="small" onClick={() => navigate('/officer/tenders/' + t.id + '/edit')}>Edit</Button>
          {t.isHistorical && (
            <Button size="small" onClick={() => navigate('/officer/award/' + t.id)}>Award</Button>
          )}
          {t.status === 'DRAFT' && <Button size="small" onClick={() => act(t.id, 'publish')}>Publish</Button>}
          {t.status === 'PUBLISHED' && <Button size="small" onClick={() => act(t.id, 'close')}>Close</Button>}
          {(t.status === 'DRAFT' || t.status === 'PUBLISHED') && (
            <Button size="small" color="error" onClick={() => act(t.id, 'cancel')}>Cancel</Button>
          )}
        </>
      ),
    },
  ];

  return (
    <DashboardLayout nav={officerNav} title="Manage Tenders">
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button variant="contained" onClick={() => navigate('/officer/tenders/new')}>Create Tender</Button>
      </Stack>

      <DataTable
        {...list}
        columns={columns}
        searchPlaceholder="Search by reference, title or school…"
        emptyMessage="No tenders yet. Create your first one."
        filterOptions={[
          { key: 'active', label: 'Open for bidding' },
          { key: 'drafts', label: 'Drafts' },
          { key: 'needsLocation', label: 'Needs location' },
          { key: 'historical', label: 'Historical records' },
        ]}
      />

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast('')}>
        <Alert severity="info" onClose={() => setToast('')}>{toast}</Alert>
      </Snackbar>
    </DashboardLayout>
  );
}